import { useEffect, useState, type FormEvent } from "react";
import { AlertCircle, Mail, Users } from "lucide-react";
import { RichEmailEditor } from "@/components/RichEmailEditor";
import { Button, FieldHint, Input, Label, Modal, ModalFooter, Select, useToast } from "@/components/ui";
import { ApiError, api } from "@/lib/api";

interface AudienceList {
  id: string;
  name: string;
  description: string | null;
  count: number;
}

interface NewCampaignDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (campaignId: string) => void;
}

export function NewCampaignDialog({ open, onClose, onCreated }: NewCampaignDialogProps) {
  const toast = useToast();
  const [lists, setLists] = useState<AudienceList[]>([]);
  const [listsLoading, setListsLoading] = useState(false);
  const [listsError, setListsError] = useState("");
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [listId, setListId] = useState("");
  const [htmlBody, setHtmlBody] = useState("");
  const [textBody, setTextBody] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setName("");
    setSubject("");
    setListId("");
    setHtmlBody("");
    setTextBody("");
    setLists([]);
    setListsError("");
    setListsLoading(true);

    api.get<{ lists: AudienceList[] }>("/lists")
      .then((data) => {
        if (!cancelled) setLists(data.lists);
      })
      .catch((err: unknown) => {
        if (!cancelled) setListsError(err instanceof ApiError ? err.message : "Couldn't load saved lists.");
      })
      .finally(() => {
        if (!cancelled) setListsLoading(false);
      });

    return () => { cancelled = true; };
  }, [open]);

  const selectedList = lists.find((list) => list.id === listId);
  const canCreate = Boolean(name.trim() && subject.trim() && listId && textBody.trim() && !saving && !listsLoading);

  const createDraft = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canCreate) return;

    setSaving(true);
    try {
      const result = await api.post<{ id: string; total_recipients: number }>("/email/campaigns", {
        name: name.trim(),
        subject: subject.trim(),
        list_id: listId,
        body: textBody,
        html_body: htmlBody,
      });
      toast.success(
        result.total_recipients === 0
          ? "Campaign draft created. No contacts in this list are currently eligible."
          : `Campaign draft created for ${result.total_recipients.toLocaleString()} eligible ${result.total_recipients === 1 ? "contact" : "contacts"}.`
      );
      onCreated(result.id);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't create the campaign draft.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New email campaign"
      description="Prepare a draft for a saved contact list. Nothing will be sent until you review it and choose to send."
      size="xl"
    >
      <form onSubmit={createDraft} className="space-y-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="campaign-name" required>Campaign name</Label>
            <Input
              id="campaign-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="e.g. October member update"
              maxLength={200}
              autoFocus
            />
            <FieldHint>For your team to identify this draft.</FieldHint>
          </div>
          <div>
            <Label htmlFor="campaign-audience" required>Audience</Label>
            <Select
              id="campaign-audience"
              value={listId}
              onChange={(event) => setListId(event.target.value)}
              disabled={listsLoading || lists.length === 0}
            >
              <option value="">{listsLoading ? "Loading saved lists…" : "Choose a saved list"}</option>
              {lists.map((list) => (
                <option key={list.id} value={list.id}>
                  {list.name} · {list.count.toLocaleString()} contacts
                </option>
              ))}
            </Select>
            {listsError ? (
              <div className="mt-2 flex items-start gap-2 text-xs text-rose-300" role="alert">
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{listsError} Close and reopen this form to try again.</span>
              </div>
            ) : selectedList ? (
              <div className="mt-2 flex items-start gap-2 rounded-lg border border-zinc-800 bg-zinc-900/40 px-3 py-2.5">
                <Users className="mt-0.5 h-4 w-4 shrink-0 text-rose-300" />
                <div className="min-w-0">
                  <p className="text-xs font-medium text-zinc-200">
                    {selectedList.count.toLocaleString()} contacts in {selectedList.name}
                  </p>
                  <p className="mt-0.5 text-[11px] leading-relaxed text-zinc-400">
                    This is the saved-list size. Only contacts with an email address who are not marked Do Not Contact or opted out are included. Please confirm they may receive marketing email before sending.
                  </p>
                </div>
              </div>
            ) : (
              <FieldHint>Choose a saved Smart List for this campaign.</FieldHint>
            )}
          </div>
        </div>

        {lists.length === 0 && !listsLoading && !listsError && (
          <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 px-3 py-2.5 text-xs text-zinc-400">
            There are no saved lists yet. Create one from Smart Lists, then return here to start your campaign.
          </div>
        )}

        <div>
          <Label htmlFor="campaign-subject" required>Subject line</Label>
          <Input
            id="campaign-subject"
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            placeholder="What will readers see in their inbox?"
            maxLength={500}
          />
        </div>

        <div>
          <Label required>Email content</Label>
          <RichEmailEditor
            value={htmlBody}
            onChange={(html, text) => {
              setHtmlBody(html);
              setTextBody(text);
            }}
            placeholder="Write your campaign email…"
            minHeight="13rem"
          />
          <FieldHint>You can personalize with {"{{first_name}}"}, {"{{full_name}}"}, and {"{{email}}"}.</FieldHint>
        </div>

        <ModalFooter>
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" loading={saving} disabled={!canCreate} leftIcon={<Mail className="h-4 w-4" />}>
            Create draft
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
